// frontend/src/types/paywall.ts

/**
 * Usage status types matching backend
 */
export type UsageStatus = 'trial-active' | 'trial-expired' | 'pro-active' | 'inactive';

/**
 * Paywall modal trigger types
 */
export type PaywallTrigger = 'no-credits' | 'trial-expired' | 'manual' | null;

/**
 * Usage summary returned from backend
 * Matches backend UserUsageDocument structure
 */
export interface UsageSummary {
  uid: string;
  planId: string;
  status: UsageStatus;
  creditsRemaining: number;
  lifetimeCreditsUsed: number;
  trialEndsAt: Date | null;
  currentPeriodEndsAt: Date | null;
  totalCredits: number;  // Credit limit from billing config (not hard-coded)
}

/**
 * API response for user usage
 */
export interface UsageResponse {
  success: boolean;
  data: UsageSummary;
  message?: string;
}

/**
 * Paywall modal state
 */
export interface PaywallState {
  isOpen: boolean;
  trigger: PaywallTrigger;
  featureKey: string | null;
}

/**
 * Feature access check response
 */
export interface FeatureAccessResponse {
  canAccess: boolean;
  reason?: string;
  usage: UsageSummary;
}

/**
 * Billing configuration (for future use)
 */
export interface BillingConfig {
  trialCredits: number;
  trialDurationHours: number;
  proMonthlyCredits: number;
  promoPricing?: {
    proUsd: number;
    originalProUsd: number;
  };
}

/**
 * Usage error class for 402 Payment Required responses
 */
export class UsageLimitError extends Error {
  code: string;
  usage?: UsageSummary;

  constructor(message: string, code: string = 'usage/limit-exceeded') {
    super(message);
    this.name = 'UsageLimitError';
    this.code = code;
  }
}

/**
 * Paywall modal variant types
 */
export type PaywallModalVariant =
  | 'no-credits-trial-active'
  | 'trial-expired-with-credits'
  | 'both-expired'
  | 'pro-credits-exhausted';

/**
 * Helper to determine paywall variant
 */
export function getPaywallVariant(
  usage: UsageSummary,
  trialDaysRemaining: number | null
): PaywallModalVariant {
  const hasCredits = usage.creditsRemaining > 0;
  const isTrialActive = usage.status === 'trial-active';
  const isTrialExpired = usage.status === 'trial-expired';
  const isProActive = usage.status === 'pro-active';

  if (isProActive && !hasCredits) {
    return 'pro-credits-exhausted';
  }

  if (isTrialActive && !hasCredits) {
    return 'no-credits-trial-active';
  }

  if (isTrialExpired && hasCredits) {
    return 'trial-expired-with-credits';
  }

  if (isTrialExpired && !hasCredits) {
    return 'both-expired';
  }

  // Default fallback
  return 'both-expired';
}

/**
 * Calculate days remaining until trial expiration
 */
export function calculateTrialDaysRemaining(trialEndsAt: Date | null): number | null {
  if (!trialEndsAt) return null;

  const now = new Date();
  const endsAt = new Date(trialEndsAt);
  const diffMs = endsAt.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * Get status color based on credits remaining
 */
export function getStatusColor(
  creditsRemaining: number,
  totalCredits: number
): 'green' | 'yellow' | 'red' {
  if (creditsRemaining === 0) return 'red';

  const percentageRemaining = (creditsRemaining / totalCredits) * 100;

  if (percentageRemaining > 80) return 'green';
  if (percentageRemaining > 20) return 'yellow';
  return 'red';
}

/**
 * REMOVED: getTotalCredits() - Hard-coded values replaced with API response.
 *
 * Credit limits are now fetched from Firestore billing config via the
 * getUserUsage endpoint. This ensures the UI stays in sync with config
 * updates without requiring code deployment.
 *
 * See: UsageSummary.totalCredits field
 */
